import { Link } from 'react-router-dom';
import { useState, type FormEvent } from 'react';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';

const FORM_URL = 'https://readdy.ai/api/form/d9rdfucnlsngrm4lmalg';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于九游娱乐',
  description: '九游娱乐是领先的在线娱乐平台，致力于为全球玩家提供安全、公平、优质的游戏体验。',
  about: {
    '@type': 'Organization',
    name: '九游娱乐',
    url: 'https://www.jiuyouyule.com/',
    description: '九游娱乐是领先的在线娱乐平台，提供精品游戏、会员福利与顶级娱乐体验。',
    foundingDate: '2018',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+86-400-888-9999',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  },
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.jiuyouyule.com/' },
    { '@type': 'ListItem', position: 2, name: '关于我们' },
  ],
};

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  useDocumentMeta(
    '关于我们 - 九游娱乐',
    '九游娱乐成立于2018年，是领先的在线娱乐平台。了解九游娱乐的品牌故事、信誉资质、核心团队和联系方式，感受我们对品质与安全的承诺。'
  );

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('idle');
    setFormMessage('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('phone_alt') as string || '').trim();
    if (honeypotValue) {
      setFormStatus('success');
      setFormMessage('消息已发送，我们会尽快回复您！');
      form.reset();
      return;
    }

    formData.delete('phone_alt');

    try {
      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse errors
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('消息已发送，我们会尽快回复您！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        setFormStatus('error');
        setFormMessage(serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查网络连接后重试');
    }
  };

  return (
    <>
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <section className="pt-24 pb-10 bg-background-100">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
              <Breadcrumb items={[{ label: '关于我们' }]} />
              <div className="mt-6 mb-2">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-100/70 text-primary-700 text-xs font-semibold mb-3">
                  <i className="ri-building-line"></i>
                  关于我们
                </div>
                <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-950 mb-3">
                  关于九游娱乐
                </h1>
                <p className="text-foreground-600 text-sm md:text-base max-w-xl">
                  以品质铸就信任，以创新引领未来
                </p>
              </div>
            </div>
          </section>

          <section className="w-full py-16 bg-background-50">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
                <div>
                  <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-5">
                    品牌故事
                  </h2>
                  <div className="flex flex-col gap-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                    <p>
                      九游娱乐成立于2018年，总部位于中国北京。自创立以来，我们始终秉持"用户至上、品质为先"的经营理念，致力于为全球玩家打造安全、公平、优质的在线娱乐平台。
                    </p>
                    <p>
                      经过多年的深耕发展，九游娱乐已成长为行业领先的综合性娱乐平台，累计服务超过5000万注册用户，提供2000+款精品游戏，涵盖棋牌、电子游艺、捕鱼、体育竞猜和真人视讯等多个品类。
                    </p>
                    <p>
                      我们拥有一支由500余名行业精英组成的专业团队，涵盖产品研发、运营服务、安全风控和客户支持等领域。团队核心成员均来自国内外知名互联网和游戏企业，拥有丰富的行业经验和技术积累。
                    </p>
                    <p>
                      未来，九游娱乐将继续坚持技术创新和服务升级，不断丰富游戏内容，提升用户体验，致力于成为全球最受信赖的在线娱乐平台。
                    </p>
                  </div>
                </div>
                <div className="aspect-[4/3] rounded-xl overflow-hidden border border-background-200/70">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20luxurious%20office%20lobby%20with%20warm%20golden%20lighting%2C%20elegant%20reception%20desk%2C%20contemporary%20Chinese%20design%20elements%2C%20professional%20corporate%20atmosphere%2C%20clean%20minimal%20composition%2C%20warm%20inviting%20feel&width=800&height=600&seq=about-brand-story&orientation=landscape"
                    alt="九游娱乐品牌故事"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </section>

          <section id="credentials" className="w-full py-16 bg-background-100">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-3">
                  信誉与资质
                </h2>
                <p className="text-foreground-600 text-sm md:text-base max-w-lg mx-auto">
                  权威认证，值得信赖。九游娱乐始终坚持合法合规运营
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
                {[
                  { icon: 'ri-shield-check-line', title: '正规运营资质', desc: '持有国家相关部门颁发的合法经营许可证，所有业务均在监管框架内合规运营。' },
                  { icon: 'ri-verified-badge-line', title: '国际安全认证', desc: '通过ISO 27001信息安全管理和GLI国际游戏实验室认证，保障平台安全可靠。' },
                  { icon: 'ri-auction-line', title: '公平游戏认证', desc: '所有游戏采用国际标准RNG随机数生成器，定期接受第三方独立审计，确保公平公正。' },
                  { icon: 'ri-bank-card-line', title: '资金安全保障', desc: '与多家持牌金融机构合作，用户资金独立托管，多重加密技术保障交易安全。' },
                ].map((item, idx) => (
                  <div key={idx} className="bg-background-50 border border-background-200/70 rounded-xl p-6 text-center hover:border-primary-200 transition-all duration-300">
                    <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-primary-100/70">
                      <i className={`${item.icon} text-2xl text-primary-600`}></i>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-900 mb-2.5">{item.title}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>

              <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-5">
                {[
                  { value: '2018年', label: '创立年份' },
                  { value: '5000万+', label: '累计用户' },
                  { value: '500+', label: '团队成员' },
                  { value: '200+', label: '合作伙伴' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center p-5 rounded-xl bg-background-50 border border-background-200/70">
                    <div className="text-2xl font-bold font-heading text-primary-600">{stat.value}</div>
                    <div className="text-sm text-foreground-500 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section id="team" className="w-full py-16 bg-background-50">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-3">
                  核心团队
                </h2>
                <p className="text-foreground-600 text-sm md:text-base max-w-lg mx-auto">
                  汇聚行业精英，以专业铸就卓越
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
                {[
                  { name: '张明远', role: '创始人 & CEO', desc: '前某知名互联网公司副总裁，15年游戏行业经验，致力于打造最优质的娱乐平台。', image: 'https://readdy.ai/api/search-image?query=Professional%20Chinese%20businessman%20portrait%2C%20middle-aged%20man%20in%20dark%20suit%2C%20confident%20expression%2C%20warm%20studio%20lighting%2C%20clean%20minimal%20background%2C%20corporate%20headshot%20style&width=400&height=400&seq=team-ceo&orientation=squarish' },
                  { name: '李思远', role: 'CTO 技术总监', desc: '前BAT高级技术专家，人工智能与大数据领域权威，主导平台核心技术架构。', image: 'https://readdy.ai/api/search-image?query=Professional%20Chinese%20tech%20executive%20portrait%2C%20man%20in%20casual%20blazer%2C%20friendly%20smile%2C%20modern%20office%20background%2C%20warm%20lighting%2C%20clean%20corporate%20headshot&width=400&height=400&seq=team-cto&orientation=squarish' },
                  { name: '王雨婷', role: 'COO 运营总监', desc: '资深游戏运营专家，曾成功运营多款千万级DAU产品，擅长用户增长与社区运营。', image: 'https://readdy.ai/api/search-image?query=Professional%20Chinese%20businesswoman%20portrait%2C%20woman%20in%20elegant%20blazer%2C%20confident%20professional%20look%2C%20warm%20studio%20lighting%2C%20clean%20minimal%20background%2C%20corporate%20headshot&width=400&height=400&seq=team-coo&orientation=squarish' },
                  { name: '陈浩然', role: 'CRO 风控总监', desc: '前国际知名风控公司高级顾问，拥有丰富的反欺诈和安全风控经验。', image: 'https://readdy.ai/api/search-image?query=Professional%20Chinese%20man%20portrait%2C%20serious%20expression%2C%20dark%20suit%2C%20corporate%20headshot%20style%2C%20warm%20studio%20lighting%2C%20clean%20minimal%20background&width=400&height=400&seq=team-cro&orientation=squarish' },
                ].map((member) => (
                  <div key={member.name} className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-200 transition-all duration-300">
                    <div className="aspect-square overflow-hidden">
                      <img src={member.image} alt={member.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="p-5 text-center">
                      <h3 className="text-base font-semibold text-foreground-900">{member.name}</h3>
                      <p className="text-sm text-primary-600 font-medium mb-2.5">{member.role}</p>
                      <p className="text-xs text-foreground-600 leading-relaxed">{member.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section id="contact" className="w-full py-16 bg-background-100">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-3">
                  联系我们
                </h2>
                <p className="text-foreground-600 text-sm md:text-base max-w-lg mx-auto">
                  7×24小时贴心服务，随时为您解答任何问题
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 bg-background-50 border border-background-200/70 rounded-xl p-6 md:p-8">
                  <h3 className="text-lg font-semibold text-foreground-900 mb-6">发送消息</h3>
                  <form className="flex flex-col gap-4" onSubmit={handleSubmit} data-readdy-form="true">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                        <input type="text" name="name" className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300/60 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors" placeholder="请输入您的姓名" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                        <input type="email" name="email" className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300/60 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors" placeholder="请输入您的邮箱" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                      <input type="text" name="subject" className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300/60 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors" placeholder="请输入消息主题" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground-700 mb-1.5">消息内容</label>
                      <textarea name="message" rows={4} maxLength={500} className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300/60 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none" placeholder="请输入您的消息内容（最多500字）"></textarea>
                    </div>
                    <div className="hp-field-wrap">
                      <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                    </div>
                    {formStatus === 'success' && (
                      <div className="px-4 py-3 rounded-lg bg-accent-100 text-accent-800 text-sm font-medium">
                        <i className="ri-check-line mr-1.5"></i>
                        {formMessage}
                      </div>
                    )}
                    {formStatus === 'error' && (
                      <div className="px-4 py-3 rounded-lg bg-primary-100 text-primary-800 text-sm font-medium">
                        <i className="ri-error-warning-line mr-1.5"></i>
                        {formMessage}
                      </div>
                    )}
                    <button type="submit" className="px-6 py-2.5 bg-primary-500 text-background-50 font-medium rounded-lg hover:bg-primary-600 transition-colors duration-200 text-sm whitespace-nowrap self-start">
                      发送消息
                      <i className="ri-send-plane-line ml-2"></i>
                    </button>
                  </form>
                </div>

                <div className="flex flex-col gap-4">
                  {[
                    { icon: 'ri-phone-line', title: '客服热线', value: '400-888-9999', desc: '7×24小时全天候服务' },
                    { icon: 'ri-mail-line', title: '电子邮箱', value: 'service@jiuyouyule.com', desc: '工作日24小时内回复' },
                    { icon: 'ri-map-pin-line', title: '公司地址', value: '北京市朝阳区建国路88号', desc: 'SOHO现代城A座' },
                  ].map((item, idx) => (
                    <div key={idx} className="bg-background-50 border border-background-200/70 rounded-xl p-5">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100/70 mb-3">
                        <i className={`${item.icon} text-lg text-primary-600`}></i>
                      </div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-1">{item.title}</h4>
                      <p className="text-sm font-medium text-primary-600 mb-0.5">{item.value}</p>
                      <p className="text-xs text-foreground-500">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
    </>
  );
}